import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, staticUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Clock, CheckCircle2, AlertCircle, FileText, Plus } from "lucide-react";

const STATUS_META = {
    pending_review: { label: "Pending Review", icon: Clock, color: "text-amber-300", border: "border-amber-300/40" },
    approved: { label: "Approved", icon: CheckCircle2, color: "text-emerald-300", border: "border-emerald-300/40" },
    published: { label: "Published", icon: CheckCircle2, color: "text-emerald-300", border: "border-emerald-300/40" },
    rejected: { label: "Rejected", icon: AlertCircle, color: "text-rose-400", border: "border-rose-400/40" },
    needs_changes: { label: "Needs Changes", icon: AlertCircle, color: "text-amber-300", border: "border-amber-300/40" },
};

const FALLBACK = "https://static.prod-images.emergentagent.com/jobs/fd94d242-06a5-4af0-93ba-0c141315848a/images/9bfbab4035d0ff1ff074c7aafc01237c0a827a32f863078809cf41a80484f384.png";

export default function MySubmissions() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !user) {
            navigate("/login");
            return;
        }
        if (user) {
            api.get("/encounters/mine/list")
                .then((r) => setData(r.data))
                .finally(() => setLoading(false));
        }
    }, [user, authLoading, navigate]);

    if (loading || authLoading) {
        return <div className="ok-container py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    }

    const items = data?.items || [];
    const tally = data?.tally || {};

    return (
        <div className="ok-container py-10 max-w-5xl mx-auto" data-testid="my-submissions-page">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">Your Archive</div>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">My Submissions</h1>
                    <p className="text-sm opacity-60 mt-1">Track the status of every encounter you&apos;ve reported.</p>
                </div>
                <Link to="/lodge" className="ok-btn-primary" data-testid="my-submissions-new">
                    <Plus className="w-4 h-4" /> Lodge new report
                </Link>
            </div>

            {/* Status tally */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="my-submissions-tally">
                {Object.entries(STATUS_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    return (
                        <div key={key} className={`ok-card p-3 ${meta.border}`} data-testid={`tally-${key}`}>
                            <div className="flex items-center gap-2">
                                <Icon className={`w-4 h-4 ${meta.color}`} />
                                <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">{meta.label}</span>
                            </div>
                            <div className="font-display text-2xl mt-1">{tally[key] || 0}</div>
                        </div>
                    );
                })}
            </div>

            {/* Submissions list */}
            {items.length === 0 ? (
                <div className="ok-card p-10 text-center" data-testid="my-submissions-empty">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <h3 className="font-display text-xl mb-1">No submissions yet</h3>
                    <p className="text-sm opacity-60 mb-5">Your first witness report is waiting to be filed.</p>
                    <Link to="/lodge" className="ok-btn-primary inline-flex" data-testid="my-submissions-empty-cta">
                        <Plus className="w-4 h-4" /> Lodge your first encounter
                    </Link>
                </div>
            ) : (
                <div className="space-y-3" data-testid="my-submissions-list">
                    {items.map((e) => {
                        const meta = STATUS_META[e.status] || STATUS_META.pending_review;
                        const Icon = meta.icon;
                        const img = staticUrl(e.thumbnail_url || e.sketch_url) || FALLBACK;
                        return (
                            <Link
                                to={`/encounters/${e.id}`}
                                key={e.id}
                                className="ok-card ok-card-hover p-3 flex items-center gap-4"
                                data-testid={`my-submission-${e.id}`}
                            >
                                <div className="w-20 h-20 rounded-sm overflow-hidden flex-shrink-0 bg-[var(--ok-border)]">
                                    <img src={img} alt={e.title} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display text-base sm:text-lg truncate">{e.title}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-1">
                                        {(e.encounter_date || e.created_at || "").slice(0, 10)} · {e.location || "Unknown location"}
                                    </div>
                                </div>
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border ${meta.border} ${meta.color} font-mono text-[10px] uppercase tracking-widest flex-shrink-0`}>
                                    <Icon className="w-3 h-3" />
                                    {meta.label}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
